import React, { useCallback, useEffect, useMemo, useRef, useState } from "react";
import { useTranslation } from "react-i18next";
import { Link, useSearchParams } from "react-router-dom";
import { toast, ToastContainer } from "react-toastify";
import "react-toastify/dist/ReactToastify.css";
import {
  Badge,
  Button,
  Card,
  CardBody,
  CardHeader,
  Col,
  Container,
  FormGroup,
  Input,
  Label,
  Offcanvas,
  OffcanvasBody,
  OffcanvasHeader,
  Pagination,
  PaginationItem,
  PaginationLink,
  Row,
  Spinner,
  Table,
} from "reactstrap";
import Swal from "sweetalert2";

import BreadCrumb from "../../../../Components/Common/BreadCrumb";
import TableContainer from "../../../../Components/Common/TableContainer";
import {
  AdatisAccount,
  batchDeleteOrphanAccounts,
  deleteAccount,
  downloadOrphanAccountsCsv,
  getAccount,
  getAccounts,
  invalidateAccountTokens,
  revokeAccountGoogle,
} from "../../../../services/superAdmin/adminAccountService";
import { PaginationMeta } from "../../../../types/api";

const PER_PAGE = 15;
const FILTER_DEBOUNCE_MS = 300;
const FORCE_DELETE_PHRASE = "DELETE_ACCOUNT";
const BATCH_DELETE_PHRASE = "DELETE_ORPHANS";
const BATCH_DELETE_MAX = 50;
const ORPHAN_CSV_EXPORT_AT_KEY = "adatis.sa.accounts.orphanCsvExportedAt";

const readStoredExportAt = (): string | null => {
  try {
    const raw = sessionStorage.getItem(ORPHAN_CSV_EXPORT_AT_KEY);
    if (!raw || Number.isNaN(Date.parse(raw))) {
      return null;
    }
    return raw;
  } catch {
    return null;
  }
};

const SuperAdminAccounts = () => {
  const { t, i18n } = useTranslation();
  const [searchParams, setSearchParams] = useSearchParams();
  const deepLinkHandled = useRef<string | null>(null);

  const [rows, setRows] = useState<AdatisAccount[]>([]);
  const [meta, setMeta] = useState<PaginationMeta | null>(null);
  const [page, setPage] = useState(1);
  const [search, setSearch] = useState("");
  const [debouncedSearch, setDebouncedSearch] = useState("");
  const [orphanOnly, setOrphanOnly] = useState(() => {
    const raw = searchParams.get("orphan");
    return raw === "1" || raw === "true";
  });
  const [loading, setLoading] = useState(true);
  const [detailOpen, setDetailOpen] = useState(false);
  const [detailLoading, setDetailLoading] = useState(false);
  const [detail, setDetail] = useState<AdatisAccount | null>(null);
  const [actionBusy, setActionBusy] = useState(false);
  const [exporting, setExporting] = useState(false);
  const [selectedIds, setSelectedIds] = useState<number[]>([]);
  const [orphanCsvExportedAt, setOrphanCsvExportedAt] = useState<string | null>(
    () => readStoredExportAt()
  );

  useEffect(() => {
    document.title = t("admin.accounts.pageTitle");
  }, [t, i18n.language]);

  const openDetail = useCallback(async (accountId: number) => {
    setDetailOpen(true);
    setDetailLoading(true);
    setDetail(null);
    try {
      const response = await getAccount(accountId);
      setDetail(response.data);
    } catch {
      toast.error(t("admin.accounts.toast.loadDetailError"));
      setDetailOpen(false);
    } finally {
      setDetailLoading(false);
    }
  }, [t]);

  useEffect(() => {
    const raw = searchParams.get("account_id");
    if (!raw || !/^\d+$/.test(raw)) {
      return;
    }
    if (deepLinkHandled.current === raw) {
      return;
    }
    deepLinkHandled.current = raw;
    void openDetail(Number(raw));
    const next = new URLSearchParams(searchParams);
    next.delete("account_id");
    setSearchParams(next, { replace: true });
  }, [searchParams, setSearchParams, openDetail]);

  useEffect(() => {
    const timer = setTimeout(
      () => setDebouncedSearch(search.trim()),
      FILTER_DEBOUNCE_MS
    );
    return () => clearTimeout(timer);
  }, [search]);

  useEffect(() => {
    setPage(1);
  }, [debouncedSearch, orphanOnly]);

  useEffect(() => {
    setSelectedIds([]);
  }, [page, debouncedSearch, orphanOnly]);

  useEffect(() => {
    const next = new URLSearchParams(searchParams);

    if (orphanOnly) {
      next.set("orphan", "1");
    } else {
      next.delete("orphan");
    }

    if ((searchParams.get("orphan") === "1") === orphanOnly) {
      return;
    }

    setSearchParams(next, { replace: true });
  }, [orphanOnly, searchParams, setSearchParams]);

  const formatDateTime = useCallback(
    (value: string | null | undefined) => {
      if (!value) {
        return t("admin.accounts.notAvailable");
      }
      return new Intl.DateTimeFormat(i18n.language, {
        dateStyle: "short",
        timeStyle: "short",
      }).format(new Date(value));
    },
    [i18n.language, t]
  );

  const loadRows = useCallback(async () => {
    setLoading(true);
    try {
      const response = await getAccounts({
        page,
        per_page: PER_PAGE,
        search: debouncedSearch || undefined,
        orphan: orphanOnly ? true : undefined,
      });
      setRows(response.data ?? []);
      setMeta(response.meta ?? null);
    } catch {
      toast.error(t("admin.accounts.toast.loadError"));
      setRows([]);
      setMeta(null);
    } finally {
      setLoading(false);
    }
  }, [page, debouncedSearch, orphanOnly, t]);

  useEffect(() => {
    void loadRows();
  }, [loadRows]);

  const orphanRowsOnPage = useMemo(
    () => rows.filter((row) => row.is_orphan),
    [rows]
  );

  const selectableOnPage = useMemo(
    () => (orphanOnly ? orphanRowsOnPage : []),
    [orphanOnly, orphanRowsOnPage]
  );
  const allPageSelected =
    selectableOnPage.length > 0 &&
    selectableOnPage.every((row) => selectedIds.includes(row.id));

  const toggleSelect = useCallback(
    (accountId: number, checked: boolean) => {
      setSelectedIds((current) => {
        if (checked) {
          if (current.includes(accountId)) {
            return current;
          }
          if (current.length >= BATCH_DELETE_MAX) {
            toast.error(
              t("admin.accounts.toast.batchLimit", { max: BATCH_DELETE_MAX })
            );
            return current;
          }
          return [...current, accountId];
        }
        return current.filter((id) => id !== accountId);
      });
    },
    [t]
  );

  const toggleSelectPage = useCallback(
    (checked: boolean) => {
      if (!checked) {
        const pageIds = new Set(selectableOnPage.map((row) => row.id));
        setSelectedIds((current) => current.filter((id) => !pageIds.has(id)));
        return;
      }

      setSelectedIds((current) => {
        const next = new Set(current);
        for (const row of selectableOnPage) {
          if (next.size >= BATCH_DELETE_MAX) {
            toast.error(
              t("admin.accounts.toast.batchLimit", { max: BATCH_DELETE_MAX })
            );
            break;
          }
          next.add(row.id);
        }
        return Array.from(next);
      });
    },
    [selectableOnPage, t]
  );

  const handleExportOrphans = async () => {
    setExporting(true);
    try {
      const blob = await downloadOrphanAccountsCsv({
        search: debouncedSearch || undefined,
      });
      const url = URL.createObjectURL(blob);
      const anchor = document.createElement("a");
      anchor.href = url;
      anchor.download = "orphan-accounts.csv";
      document.body.appendChild(anchor);
      anchor.click();
      anchor.remove();
      URL.revokeObjectURL(url);
      const exportedAt = new Date().toISOString();
      setOrphanCsvExportedAt(exportedAt);
      try {
        sessionStorage.setItem(ORPHAN_CSV_EXPORT_AT_KEY, exportedAt);
      } catch {
        // sessionStorage pode falhar em modo restrito — badge ainda vale nesta sessão
      }
      toast.success(t("admin.accounts.toast.exportReady"));
    } catch {
      toast.error(t("admin.accounts.toast.exportError"));
    } finally {
      setExporting(false);
    }
  };

  const handleBatchDelete = async () => {
    if (selectedIds.length === 0) {
      toast.error(t("admin.accounts.toast.batchEmpty"));
      return;
    }
    if (selectedIds.length > BATCH_DELETE_MAX) {
      toast.error(
        t("admin.accounts.toast.batchLimit", { max: BATCH_DELETE_MAX })
      );
      return;
    }

    const exportHint = orphanCsvExportedAt
      ? `<p class="text-muted small mb-2">${t(
          "admin.accounts.deleteBatch.exportDoneHint",
          { time: formatDateTime(orphanCsvExportedAt) }
        )}</p>`
      : `<p class="text-warning fw-semibold mb-2">${t(
          "admin.accounts.deleteBatch.exportMissingHint"
        )}</p>`;

    const result = await Swal.fire({
      title: t("admin.accounts.deleteBatch.title"),
      html: `${exportHint}<p>${t("admin.accounts.deleteBatch.description", {
        count: selectedIds.length,
      })}</p><p class="text-danger fw-semibold">${t(
        "admin.accounts.deleteBatch.warning"
      )}</p>`,
      input: "text",
      inputPlaceholder: BATCH_DELETE_PHRASE,
      inputValidator: (value) =>
        value === BATCH_DELETE_PHRASE
          ? null
          : t("admin.accounts.deleteBatch.phraseMismatch"),
      icon: "error",
      showCancelButton: true,
      confirmButtonText: t("admin.accounts.deleteBatch.confirm"),
      cancelButtonText: t("admin.accounts.actions.cancel"),
    });

    if (!result.isConfirmed) {
      return;
    }

    setActionBusy(true);
    try {
      const response = await batchDeleteOrphanAccounts(
        selectedIds,
        BATCH_DELETE_PHRASE
      );
      toast.success(
        t("admin.accounts.toast.batchDeleted", {
          count: response.data.deleted_count,
        })
      );
      setSelectedIds([]);
      setDetailOpen(false);
      await loadRows();
    } catch (error: any) {
      toast.error(
        error?.response?.data?.error?.message ||
          t("admin.accounts.toast.batchDeleteError")
      );
    } finally {
      setActionBusy(false);
    }
  };

  const handleDelete = async (account: AdatisAccount) => {
    if (account.is_orphan) {
      const result = await Swal.fire({
        title: t("admin.accounts.deleteOrphan.title"),
        text: t("admin.accounts.deleteOrphan.description", {
          email: account.email,
        }),
        icon: "warning",
        showCancelButton: true,
        confirmButtonText: t("admin.accounts.deleteOrphan.confirm"),
        cancelButtonText: t("admin.accounts.actions.cancel"),
      });
      if (!result.isConfirmed) {
        return;
      }
      setActionBusy(true);
      try {
        await deleteAccount(account.id);
        toast.success(t("admin.accounts.toast.deleted"));
        setDetailOpen(false);
        setSelectedIds((current) =>
          current.filter((id) => id !== account.id)
        );
        await loadRows();
      } catch (error: any) {
        toast.error(
          error?.response?.data?.error?.message ||
            t("admin.accounts.toast.deleteError")
        );
      } finally {
        setActionBusy(false);
      }
      return;
    }

    const result = await Swal.fire({
      title: t("admin.accounts.deleteForce.title"),
      html: `<p>${t("admin.accounts.deleteForce.description", {
        email: account.email,
        count: account.memberships_count,
      })}</p><p class="text-danger fw-semibold">${t(
        "admin.accounts.deleteForce.warning"
      )}</p>`,
      input: "text",
      inputPlaceholder: FORCE_DELETE_PHRASE,
      inputValidator: (value) =>
        value === FORCE_DELETE_PHRASE
          ? null
          : t("admin.accounts.deleteForce.phraseMismatch"),
      icon: "error",
      showCancelButton: true,
      confirmButtonText: t("admin.accounts.deleteForce.confirm"),
      cancelButtonText: t("admin.accounts.actions.cancel"),
    });

    if (!result.isConfirmed) {
      return;
    }

    setActionBusy(true);
    try {
      await deleteAccount(account.id, FORCE_DELETE_PHRASE);
      toast.success(t("admin.accounts.toast.deleted"));
      setDetailOpen(false);
      await loadRows();
    } catch (error: any) {
      toast.error(
        error?.response?.data?.error?.message ||
          t("admin.accounts.toast.deleteError")
      );
    } finally {
      setActionBusy(false);
    }
  };

  const handleRevokeGoogle = async (account: AdatisAccount) => {
    const result = await Swal.fire({
      title: t("admin.accounts.revokeGoogle.title"),
      text: t("admin.accounts.revokeGoogle.description"),
      icon: "warning",
      showCancelButton: true,
      confirmButtonText: t("admin.accounts.revokeGoogle.confirm"),
      cancelButtonText: t("admin.accounts.actions.cancel"),
    });
    if (!result.isConfirmed) {
      return;
    }
    setActionBusy(true);
    try {
      const response = await revokeAccountGoogle(account.id);
      setDetail(response.data);
      toast.success(t("admin.accounts.toast.googleRevoked"));
      await loadRows();
    } catch (error: any) {
      toast.error(
        error?.response?.data?.error?.message ||
          t("admin.accounts.toast.googleRevokeError")
      );
    } finally {
      setActionBusy(false);
    }
  };

  const handleInvalidateTokens = async (account: AdatisAccount) => {
    const result = await Swal.fire({
      title: t("admin.accounts.invalidateTokens.title"),
      text: t("admin.accounts.invalidateTokens.description"),
      icon: "warning",
      showCancelButton: true,
      confirmButtonText: t("admin.accounts.invalidateTokens.confirm"),
      cancelButtonText: t("admin.accounts.actions.cancel"),
    });
    if (!result.isConfirmed) {
      return;
    }
    setActionBusy(true);
    try {
      await invalidateAccountTokens(account.id);
      toast.success(t("admin.accounts.toast.tokensInvalidated"));
    } catch {
      toast.error(t("admin.accounts.toast.tokensInvalidateError"));
    } finally {
      setActionBusy(false);
    }
  };

  const columns = useMemo(() => {
    const base = [
      ...(orphanOnly
        ? [
            {
              id: "select",
              header: () => (
                <Input
                  type="checkbox"
                  checked={allPageSelected}
                  onChange={(event) => toggleSelectPage(event.target.checked)}
                  aria-label={t("admin.accounts.actions.selectPage")}
                  onClick={(event) => event.stopPropagation()}
                />
              ),
              enableColumnFilter: false,
              enableSorting: false,
              cell: (ctx: { row: { original: AdatisAccount } }) => {
                const account = ctx.row.original;
                if (!account.is_orphan) {
                  return null;
                }
                return (
                  <Input
                    type="checkbox"
                    checked={selectedIds.includes(account.id)}
                    onChange={(event) =>
                      toggleSelect(account.id, event.target.checked)
                    }
                    aria-label={t("admin.accounts.actions.selectRow", {
                      email: account.email,
                    })}
                  />
                );
              },
            },
          ]
        : []),
      {
        header: t("admin.accounts.table.id"),
        accessorKey: "id",
        enableColumnFilter: false,
        cell: (ctx: { row: { original: AdatisAccount } }) => ctx.row.original.id,
      },
      {
        header: t("admin.accounts.table.name"),
        accessorKey: "name",
        enableColumnFilter: false,
        cell: (ctx: { row: { original: AdatisAccount } }) =>
          ctx.row.original.name || t("admin.accounts.notAvailable"),
      },
      {
        header: t("admin.accounts.table.email"),
        accessorKey: "email",
        enableColumnFilter: false,
      },
      {
        header: t("admin.accounts.table.status"),
        accessorKey: "is_orphan",
        enableColumnFilter: false,
        cell: (ctx: { row: { original: AdatisAccount } }) => {
          const account = ctx.row.original;
          return (
            <span className="d-inline-flex flex-wrap gap-1">
              {account.is_orphan ? (
                <Badge color="warning" className="fs-12">
                  {t("admin.accounts.badges.orphan")}
                </Badge>
              ) : (
                <Badge color="success" className="fs-12">
                  {t("admin.accounts.badges.linked", {
                    count: account.memberships_count,
                  })}
                </Badge>
              )}
              {account.google_linked ? (
                <Badge color="info" className="fs-12">
                  {t("admin.accounts.badges.google")}
                </Badge>
              ) : null}
            </span>
          );
        },
      },
      {
        header: t("admin.accounts.table.createdAt"),
        accessorKey: "created_at",
        enableColumnFilter: false,
        cell: (ctx: { row: { original: AdatisAccount } }) =>
          formatDateTime(ctx.row.original.created_at),
      },
      {
        header: t("admin.accounts.table.actions"),
        accessorKey: "actions",
        enableColumnFilter: false,
        cell: (ctx: { row: { original: AdatisAccount } }) => (
          <Button
            color="soft-primary"
            size="sm"
            onClick={() => void openDetail(ctx.row.original.id)}
          >
            {t("admin.accounts.actions.details")}
          </Button>
        ),
      },
    ];

    return base;
  }, [
    orphanOnly,
    allPageSelected,
    selectedIds,
    formatDateTime,
    t,
    openDetail,
    toggleSelect,
    toggleSelectPage,
  ]);

  const totalPages = meta?.last_page ?? 1;

  return (
    <div className="page-content">
      <Container fluid>
        <BreadCrumb
          title={t("admin.accounts.breadcrumb.title")}
          pageTitle={t("admin.accounts.breadcrumb.pageTitle")}
        />
        <Row>
          <Col>
            <Card>
              <CardHeader className="d-flex flex-wrap justify-content-between gap-2 align-items-center">
                <div>
                  <h4 className="card-title mb-1">
                    {t("admin.accounts.card.title")}
                  </h4>
                  <p className="text-muted mb-0">
                    {t("admin.accounts.card.description")}
                  </p>
                </div>
                <div className="d-flex flex-wrap gap-2 align-items-center">
                  {orphanCsvExportedAt ? (
                    <Badge color="success" className="fs-12">
                      {t("admin.accounts.exportBadge.downloadedAt", {
                        time: formatDateTime(orphanCsvExportedAt),
                      })}
                    </Badge>
                  ) : (
                    <Badge color="light" className="fs-12 text-muted">
                      {t("admin.accounts.exportBadge.notYet")}
                    </Badge>
                  )}
                  <Button
                    color="soft-secondary"
                    disabled={exporting || loading}
                    onClick={() => void handleExportOrphans()}
                  >
                    {exporting ? (
                      <Spinner size="sm" />
                    ) : (
                      <>
                        <i className="ri-download-2-line align-bottom me-1" />
                        {t("admin.accounts.actions.exportOrphans")}
                      </>
                    )}
                  </Button>
                  {orphanOnly ? (
                    <Button
                      color="danger"
                      disabled={
                        actionBusy || loading || selectedIds.length === 0
                      }
                      onClick={() => void handleBatchDelete()}
                    >
                      {t("admin.accounts.actions.deleteSelected", {
                        count: selectedIds.length,
                      })}
                    </Button>
                  ) : null}
                </div>
              </CardHeader>
              <CardBody>
                <Row className="g-3 mb-3">
                  <Col md={6}>
                    <Label className="form-label">
                      {t("admin.accounts.filters.search")}
                    </Label>
                    <Input
                      value={search}
                      onChange={(event) => setSearch(event.target.value)}
                      placeholder={t("admin.accounts.filters.searchPlaceholder")}
                    />
                  </Col>
                  <Col md={6} className="d-flex align-items-end">
                    <FormGroup check className="mb-2">
                      <Input
                        id="orphanOnly"
                        type="checkbox"
                        checked={orphanOnly}
                        onChange={(event) =>
                          setOrphanOnly(event.target.checked)
                        }
                      />
                      <Label check htmlFor="orphanOnly">
                        {t("admin.accounts.filters.orphanOnly")}
                      </Label>
                    </FormGroup>
                  </Col>
                </Row>

                {orphanOnly ? (
                  <div className="d-flex flex-wrap align-items-center gap-2 mb-3">
                    <span className="text-muted small">
                      {selectedIds.length > 0
                        ? t("admin.accounts.selection.summary", {
                            count: selectedIds.length,
                            max: BATCH_DELETE_MAX,
                          })
                        : t("admin.accounts.selection.hint")}
                    </span>
                    <Button
                      color="soft-primary"
                      size="sm"
                      disabled={
                        loading ||
                        selectableOnPage.length === 0 ||
                        allPageSelected
                      }
                      onClick={() => toggleSelectPage(true)}
                    >
                      {t("admin.accounts.actions.selectPage")}
                    </Button>
                    {selectedIds.length > 0 ? (
                      <Button
                        color="link"
                        size="sm"
                        className="p-0"
                        onClick={() => setSelectedIds([])}
                      >
                        {t("admin.accounts.actions.clearSelection")}
                      </Button>
                    ) : null}
                  </div>
                ) : null}

                {loading ? (
                  <div className="d-flex justify-content-center py-5">
                    <Spinner color="primary" />
                  </div>
                ) : rows.length === 0 ? (
                  <div className="text-center text-muted py-5">
                    <p className="mb-1">{t("admin.accounts.empty.title")}</p>
                    <p className="mb-0 small">
                      {t("admin.accounts.empty.hint")}
                    </p>
                  </div>
                ) : (
                  <>
                    <TableContainer
                      columns={columns}
                      data={rows}
                      isGlobalFilter={false}
                      customPageSize={PER_PAGE}
                      tableClass="table-nowrap align-middle mb-0"
                      theadClass="table-light"
                    />
                    {totalPages > 1 ? (
                      <div className="d-flex justify-content-end mt-3">
                        <Pagination className="pagination-separated mb-0">
                          <PaginationItem disabled={page <= 1}>
                            <PaginationLink
                              previous
                              onClick={() => setPage((current) => current - 1)}
                            />
                          </PaginationItem>
                          <PaginationItem active>
                            <PaginationLink>{page}</PaginationLink>
                          </PaginationItem>
                          <PaginationItem disabled={page >= totalPages}>
                            <PaginationLink
                              next
                              onClick={() => setPage((current) => current + 1)}
                            />
                          </PaginationItem>
                        </Pagination>
                      </div>
                    ) : null}
                  </>
                )}
              </CardBody>
            </Card>
          </Col>
        </Row>
      </Container>

      <Offcanvas
        direction="end"
        isOpen={detailOpen}
        toggle={() => setDetailOpen(false)}
        style={{ width: 460 }}
      >
        <OffcanvasHeader toggle={() => setDetailOpen(false)}>
          {detail?.email ?? t("admin.accounts.detail.title")}
        </OffcanvasHeader>
        <OffcanvasBody>
          {detailLoading || !detail ? (
            <div className="d-flex justify-content-center py-5">
              <Spinner color="primary" />
            </div>
          ) : (
            <>
              <p className="text-muted mb-3">
                {t("admin.accounts.detail.subtitle")}
              </p>
              <Table borderless size="sm" className="mb-3">
                <tbody>
                  <tr>
                    <td className="text-muted">{t("admin.accounts.table.name")}</td>
                    <td className="text-end fw-medium">
                      {detail.name || t("admin.accounts.notAvailable")}
                    </td>
                  </tr>
                  <tr>
                    <td className="text-muted">{t("admin.accounts.table.email")}</td>
                    <td className="text-end fw-medium">{detail.email}</td>
                  </tr>
                  <tr>
                    <td className="text-muted">
                      {t("admin.accounts.detail.password")}
                    </td>
                    <td className="text-end">
                      {detail.has_password
                        ? t("admin.accounts.detail.passwordYes")
                        : t("admin.accounts.detail.passwordNo")}
                    </td>
                  </tr>
                  <tr>
                    <td className="text-muted">
                      {t("admin.accounts.detail.google")}
                    </td>
                    <td className="text-end">
                      {detail.google_linked
                        ? detail.google_email || t("admin.accounts.badges.google")
                        : t("admin.accounts.detail.googleNo")}
                    </td>
                  </tr>
                  <tr>
                    <td className="text-muted">
                      {t("admin.accounts.detail.orphan")}
                    </td>
                    <td className="text-end">
                      {detail.is_orphan
                        ? t("admin.accounts.badges.orphan")
                        : t("admin.accounts.badges.linked", {
                            count: detail.memberships_count,
                          })}
                    </td>
                  </tr>
                </tbody>
              </Table>

              <h6 className="text-uppercase text-muted fs-12 mb-2">
                {t("admin.accounts.detail.memberships")}
              </h6>
              {(detail.memberships ?? []).length === 0 ? (
                <p className="text-muted small">
                  {t("admin.accounts.detail.noMemberships")}
                </p>
              ) : (
                <div className="d-flex flex-column gap-2 mb-3">
                  {(detail.memberships ?? []).map((membership) => (
                    <div
                      key={membership.id}
                      className="border rounded p-2 d-flex justify-content-between gap-2 align-items-start"
                    >
                      <div>
                        <div className="fw-medium">
                          {membership.tenant_name ||
                            t("admin.accounts.notAvailable")}
                        </div>
                        <div className="small text-muted">
                          {membership.roles.join(", ") ||
                            t("admin.accounts.notAvailable")}
                          {" · "}
                          {t(`admin.accounts.membershipStatus.${membership.status}`, {
                            defaultValue: membership.status,
                          })}
                        </div>
                      </div>
                      {membership.tenant_id ? (
                        <Button
                          tag={Link}
                          to={`/super-admin/tenants/${membership.tenant_id}/team`}
                          color="soft-secondary"
                          size="sm"
                        >
                          {t("admin.accounts.actions.openTeam")}
                        </Button>
                      ) : null}
                    </div>
                  ))}
                </div>
              )}

              <div className="d-flex flex-column gap-2">
                {detail.google_linked ? (
                  <Button
                    color="warning"
                    outline
                    disabled={actionBusy}
                    onClick={() => void handleRevokeGoogle(detail)}
                  >
                    {t("admin.accounts.actions.revokeGoogle")}
                  </Button>
                ) : null}
                <Button
                  color="secondary"
                  outline
                  disabled={actionBusy}
                  onClick={() => void handleInvalidateTokens(detail)}
                >
                  {t("admin.accounts.actions.invalidateTokens")}
                </Button>
                <Button
                  color="danger"
                  disabled={actionBusy}
                  onClick={() => void handleDelete(detail)}
                >
                  {detail.is_orphan
                    ? t("admin.accounts.actions.deleteOrphan")
                    : t("admin.accounts.actions.deleteForce")}
                </Button>
              </div>
            </>
          )}
        </OffcanvasBody>
      </Offcanvas>

      <ToastContainer closeButton={false} limit={1} />
    </div>
  );
};

export default SuperAdminAccounts;
